import React, { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { authService } from '../../services/authService';

const ModalLogin = ({ closeModal }) => {
  const { login, loginDoctorByCedula } = useAuth();

  const [activeTab, setActiveTab] = useState("login");
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    cedula: '',
    otp: ''
  });
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [doctorStep, setDoctorStep] = useState('cedula'); // 'cedula' | 'otp'

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    setLoginSuccess(false);

    try {
      await login(formData.email, formData.password);
      setLoginSuccess(true);
      
      // Esperar un momento para asegurar que el estado se actualice
      setTimeout(() => {
        closeModal();
      }, 300);
    } catch (err) {
      setError(err.message || 'Error al iniciar sesión');
      setLoginSuccess(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCedula = async (e) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      await authService.verifyCedula(formData.cedula);
      setDoctorStep('otp');
    } catch (err) {
      setError(err.message || 'Error al verificar la cédula');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    setLoginSuccess(false);
    try {
      await loginDoctorByCedula(formData.cedula, formData.otp);
      setLoginSuccess(true);
      setTimeout(() => {
        closeModal();
      }, 300);
    } catch (err) {
      setError(err.message || 'Error al verificar el OTP');
      setLoginSuccess(false);
    } finally {
      setIsLoading(false);
    }
  };

  const switchToDoctor = () => {
    setActiveTab('doctor');
    setDoctorStep('cedula');
    setFormData(prev => ({ ...prev, cedula: '', otp: '' }));
    setError(null);
  };

  const switchFromDoctor = () => {
    setActiveTab('login');
    setDoctorStep('cedula');
    setFormData(prev => ({ ...prev, cedula: '', otp: '' }));
  };

  return (
    <div className="flex fixed inset-0 z-50 justify-center items-center p-4 bg-black bg-opacity-50">
      <div className="bg-white w-full max-w-md rounded-xl shadow-xl relative max-h-[95vh] overflow-y-auto">
        {/* Botón cerrar */}
        <button
          onClick={closeModal}
          className="absolute top-4 right-4 text-2xl text-gray-500 hover:text-gray-700 focus:outline-none"
        >
          &times;
        </button>

        <div className="p-6 sm:p-8">
          <div className="flex justify-center mb-6">
            <img src="/img/logo.png" alt="Logo" className="object-contain w-16 h-16" />
          </div>

          {/* Tabs */}
          <div className="flex mb-6 border-b">
            <button
              className={`flex-1 py-3 text-sm sm:text-base font-medium text-center transition-colors ${
                activeTab === "login" || activeTab === "doctor"
                  ? "text-green-600 border-b-2 border-green-500"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => activeTab === "doctor" ? switchFromDoctor() : setActiveTab("login")}
            >
              Interno
            </button>
            <button
              className={`flex-1 py-3 text-sm sm:text-base font-medium text-center transition-colors ${
                activeTab === "doctor"
                  ? "text-green-600 border-b-2 border-green-500"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => setActiveTab("doctor")}
            >
              Externo
            </button>
          </div>

          {/* Error */}
          {error && (
            <div className="p-3 mb-4 text-sm text-red-600 bg-red-50 rounded-lg">
              {error}
            </div>
          )}

          {/* Estado de carga del login */}
          {loginSuccess && (
            <div className="p-3 mb-4 text-sm text-green-600 bg-green-50 rounded-lg">
              Iniciando sesión...
            </div>
          )}

          {/* Formulario doctor (cédula + OTP) */}
          {activeTab === "doctor" ? (
            doctorStep === "cedula" ? (
              <form onSubmit={handleVerifyCedula} noValidate>
                <div className="space-y-4">
                  <input
                    type="text"
                    name="cedula"
                    value={formData.cedula}
                    onChange={handleInputChange}
                    required
                    placeholder="Número de cédula"
                    className="px-4 py-3 w-full rounded-lg border"
                    disabled={isLoading}
                  />
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="py-3 w-full text-white bg-green-600 rounded-lg transition hover:bg-green-700 disabled:opacity-50"
                  >
                    {isLoading ? "Verificando..." : "Enviar OTP"}
                  </button>
                  <p className="text-xs text-gray-500">
                    Se enviará un código OTP al correo registrado.
                  </p>
                </div>
              </form>
            ) : (
              <form onSubmit={handleVerifyOtp} noValidate>
                <div className="space-y-4">
                  <input
                    type="text"
                    name="otp"
                    value={formData.otp}
                    onChange={handleInputChange}
                    required
                    placeholder="Código OTP"
                    className="px-4 py-3 w-full rounded-lg border"
                    disabled={isLoading || loginSuccess}
                    maxLength={6}
                  />
                  <button
                    type="submit"
                    disabled={isLoading || loginSuccess}
                    className="py-3 w-full text-white bg-green-600 rounded-lg transition hover:bg-green-700 disabled:opacity-50"
                  >
                    {isLoading ? "Verificando..." : loginSuccess ? "Iniciando sesión..." : "Ingresar"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setDoctorStep("cedula")}
                    className="w-full text-sm text-gray-500 hover:text-gray-700"
                  >
                    Cambiar cédula
                  </button>
                </div>
              </form>
            )
          ) : activeTab === "login" ? (
            <form onSubmit={handleLogin} noValidate>
              <div className="space-y-4">
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder="Correo electrónico"
                  className="px-4 py-3 w-full rounded-lg border"
                  disabled={isLoading || loginSuccess}
                />

                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  placeholder="Contraseña"
                  className="px-4 py-3 w-full rounded-lg border"
                  disabled={isLoading || loginSuccess}
                />

                <div className="flex justify-end">
                  <a
                    href="https://megalabs.app/password/reset"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-green-600 hover:underline"
                  >
                    ¿Olvidaste tu contraseña?
                  </a>
                </div>

                <button
                  type="submit"
                  disabled={isLoading || loginSuccess}
                  className="py-3 w-full text-white bg-green-600 rounded-lg transition hover:bg-green-700 disabled:opacity-50"
                >
                  {isLoading ? "Ingresando..." : loginSuccess ? "Iniciando sesión..." : "Ingresar"}
                </button>
              </div>
            </form>
          ) : null}

            {/* Footer */}
          <div className="mt-6 text-sm text-center text-gray-500">
            {activeTab === "login" ? (
              <p>
                ¿Eres doctor?{' '}
                <button 
                  type="button"
                  onClick={switchToDoctor}
                  className="text-green-600 rounded hover:underline focus:outline-none focus:ring-2 focus:ring-green-500"
                  disabled={isLoading || loginSuccess}
                >
                  Inicia sesión con cédula
                </button>
              </p>
            ) : (
              <p>
                <button 
                  type="button"
                  onClick={switchFromDoctor}
                  className="text-green-600 rounded hover:underline focus:outline-none focus:ring-2 focus:ring-green-500"
                  disabled={isLoading || loginSuccess}
                >
                  Volver a iniciar sesión
                </button>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalLogin;
